import {create} from 'zustand';
import {$api} from 'shared/api';
import {serializeParams} from 'shared/helpers/converters';
import {
  DeviceRow,
  DevicesState,
  GetDevicesParams,
  RenameDeviceModalState,
  UpdateDeviceType,
} from './types';
import {toast} from 'react-toastify';
import {AxiosError} from 'axios';

const toNum = (v: any) => {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const mapApiDeviceToInternal = (api: any): DeviceRow => {
  const id = String(api?.id ?? api?._id ?? '');
  const type = String(api?.type ?? api?.kind ?? api?.deviceType ?? api?.name ?? '');
  const registryName = api?.registryName ? String(api.registryName) : null;
  const name = String(api?.name || registryName || api?.label || type || '');
  const hiveId = api?.hiveId ?? api?.hive_id ?? api?.hive?.id ?? api?.clientId ?? null;
  const hiveNumber = api?.hiveNumber != null ? String(api.hiveNumber) : null;
  const hiveTitle = hiveNumber ? `Улей №${hiveNumber}` : '—';
  const batteryVoltage = toNum(
    api?.battery ?? api?.batteryVoltage ?? api?.battery_v ?? api?.batteryVolt
  );
  const updatedISO = api?.updatedAt ?? api?.lastSeenAt ?? api?.last_update ?? null;
  return {
    id,
    name,
    type,
    hiveId: hiveId ? String(hiveId) : null,
    hiveNumber,
    hiveTitle,
    batteryVoltage,
    updatedAt: updatedISO ? String(updatedISO) : '',
    updatedISO: updatedISO ? String(updatedISO) : null,
    registryName,
  };
};

const applyFilters = (list: DeviceRow[], f: DevicesState['filters']): DeviceRow[] => {
  let res = list;
  if (f.hiveNumbers.length) {
    const set = new Set(f.hiveNumbers.map(String));
    res = res.filter((d) => (d.hiveNumber ? set.has(String(d.hiveNumber)) : false));
  }
  const dir = f.sort === 'ASC' ? 1 : -1;
  const cmp = (a: DeviceRow, b: DeviceRow) => {
    if (f.sortBy === 'batteryVoltage') {
      const av = a.batteryVoltage ?? -Infinity;
      const bv = b.batteryVoltage ?? -Infinity;
      return av === bv ? 0 : av > bv ? dir : -dir;
    }
    if (f.sortBy === 'type') return a.type.localeCompare(b.type) * dir;
    if (f.sortBy === 'hive') return a.hiveTitle.localeCompare(b.hiveTitle) * dir;
    if (f.sortBy === 'hiveNumber') {
      const an = a.hiveNumber ? Number(a.hiveNumber) : Infinity;
      const bn = b.hiveNumber ? Number(b.hiveNumber) : Infinity;
      if (an === bn) return 0;
      if (f.sort === 'ASC') {
        return an > bn ? 1 : -1;
      } else {
        return an > bn ? -1 : 1;
      }
    }
    const at = a.updatedISO ? new Date(a.updatedISO).getTime() : 0;
    const bt = b.updatedISO ? new Date(b.updatedISO).getTime() : 0;
    return at === bt ? 0 : at > bt ? dir : -dir;
  };
  return [...res].sort(cmp);
};

export const useDevicesStore = create<DevicesState>((set, get) => ({
  allDevices: [],
  devices: [],
  isLoading: false,
  lastApiaryId: null,
  filters: {hiveNumbers: [], sortBy: 'hiveNumber', sort: 'ASC'},
  openState: {isOpen: false, name: null, id: null},

  getDevicesFromServer: async ({apiaryId}: GetDevicesParams) => {
    set({isLoading: true, lastApiaryId: apiaryId});
    let list: DeviceRow[] = [];
    try {
      const http = await $api.get<any>('/collector/devices', {
        params: {apiaryId},
        paramsSerializer: {serialize: serializeParams},
      });
      const arr = Array.isArray(http?.data) ? http.data : [];
      list = arr.map(mapApiDeviceToInternal);
    } catch {
      list = [];
    }

    const filtered = applyFilters(list, get().filters);
    set({allDevices: list, devices: filtered, isLoading: false});
    return filtered;
  },

  updateDevice: async (id: string, data: UpdateDeviceType) => {
    try {
      const req = await $api.patch<DeviceRow>(`/collector/devices/${id}`, data);
      return req.data;
    } catch (err) {
      if (err instanceof AxiosError) {
        toast.error(`Ошибка: ${err.message}`);
      }
    }
  },

  setHiveNumbersFilter: (nums: string[]) => {
    const filters = {...get().filters, hiveNumbers: (nums || []).map(String)};
    const next = applyFilters(get().allDevices, filters);
    set({filters, devices: next});
  },

  setSort: (sortBy, sort) => {
    const currentFilters = get().filters;
    const newSort = sort ?? currentFilters.sort;

    if (currentFilters.sortBy === sortBy && currentFilters.sort === newSort) {
      return;
    }

    const filters = {...currentFilters, sortBy, sort: newSort};
    const next = applyFilters(get().allDevices, filters);
    set({filters, devices: next});
  },

  reload: async () => {
    const apiaryId = get().lastApiaryId;
    if (!apiaryId) return;
    await get().getDevicesFromServer({apiaryId});
  },

  detachDevice: async (deviceId: string) => {
    await $api.delete(`/collector/devices/${deviceId}`);
    await get().reload();
  },

  setModalState: (val: RenameDeviceModalState) => {
    set({
      openState: {isOpen: val.isOpen || false, name: val.name, id: val.id},
    });
  },
}));
